/**
 * AutopilotSection — Sprint 7 · Project Symphony
 *
 * The "Autopilot Dashboard" — new Campaign Room section per the
 * executive patch. Composed of: Health header, Trust Level selector,
 * Human Override controls, Department Autonomy panel, and the
 * Recent Actions (living timeline) list.
 *
 * Executive amendments honored:
 *  - Every autonomous action shows WHY, WHICH RULE, and WHICH TRUST
 *    LEVEL authorised it.
 *  - Pause / Resume / Manual Override / Emergency Stop are first-
 *    class controls, always accessible.
 *  - Deterministic vocabulary — no AI-generated copy in the shell.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";

import {
  getAutopilotDashboard,
  runAutopilotTick,
  type AutopilotDashboard,
} from "../api";
import { AutopilotHealthCard } from "./AutopilotHealthCard";
import { TrustLevelSelector } from "./TrustLevelSelector";
import { HumanOverrideControls } from "./HumanOverrideControls";
import { DepartmentAutonomyPanel } from "./DepartmentAutonomyPanel";
import { DepartmentTimeline } from "./DepartmentTimeline";

export function AutopilotSection(): JSX.Element | null {
  const qc = useQueryClient();
  const query = useQuery({
    queryKey: ["autopilot-dashboard"],
    queryFn: getAutopilotDashboard,
    refetchInterval: 20_000,
  });

  const runTick = useMutation({
    mutationFn: () => runAutopilotTick(),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["autopilot-dashboard"] }),
  });

  if (query.isLoading) {
    return (
      <section
        data-testid="autopilot-loading"
        className="flex items-center gap-2 text-muted-foreground text-sm"
      >
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
        Autopilot is waking up…
      </section>
    );
  }

  const dashboard = query.data as AutopilotDashboard | undefined;
  if (!dashboard) return null;

  return (
    <section data-testid="autopilot-section" className="space-y-6">
      <div>
        <p className="text-xs uppercase tracking-widest text-muted-foreground mb-2">
          Autopilot
        </p>
        <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight">
          Your marketing department.
        </h2>
        <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
          Autopilot is a professional department — not automatic
          publishing. Every action is explained, every trust level is
          revocable, and you can pause, resume, or take manual control
          at any moment.
        </p>
      </div>

      <AutopilotHealthCard dashboard={dashboard} />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="md:col-span-2">
          <TrustLevelSelector dashboard={dashboard} />
        </div>
        <div>
          <HumanOverrideControls
            state={dashboard.state}
            onRunTick={() => runTick.mutate()}
            runTickPending={runTick.isPending}
          />
        </div>
      </div>

      <DepartmentAutonomyPanel rows={dashboard.departments_active} />

      <DepartmentTimeline actions={dashboard.recent_actions} />
    </section>
  );
}
